import { FC, useMemo, useState } from "react";
import { OrganizationBillingInvoicesViewSchema } from "@common/types/views/organizationBillingInvoicesView";
import { View } from "@common/types/views/views";
import { Loader } from "@design-system/Loader/Loader";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { useOrganizationInvoicesQuery } from "@api/billingOrganization.api";
import {
    billingInvoicesViewConfig,
    buildBillingInvoicesRows,
    createBillingInvoicesView,
} from "./billingInvoicesViewConfig";
import styles from "./BillingPageV2ClientsSection.module.scss";

interface BillingPageV2InvoiceHistoryProps {
    organizationId: string | undefined;
    isActive: boolean;
}

export const BillingPageV2InvoiceHistory: FC<
    BillingPageV2InvoiceHistoryProps
> = ({ organizationId, isActive }) => {
    const { data, isLoading, isError } = useOrganizationInvoicesQuery(
        organizationId,
        isActive,
    );
    const [view, setView] = useState(createBillingInvoicesView);

    const rows = useMemo(
        () => (data ? buildBillingInvoicesRows(data.invoices) : []),
        [data],
    );

    const handleViewChange = (nextView: View) => {
        setView(OrganizationBillingInvoicesViewSchema.parse(nextView));
    };

    if (isLoading) {
        return <Loader />;
    }

    if (isError) {
        return (
            <div className={styles.empty}>Invoice history is unavailable.</div>
        );
    }

    if (!data?.invoices.length) {
        return (
            <div className={styles.empty}>
                No invoices yet. Past invoices will show up here after the first
                billing cycle.
            </div>
        );
    }

    return (
        <ViewTable
            data={rows}
            view={view}
            config={billingInvoicesViewConfig}
            onViewChange={handleViewChange}
            getRowId={(row) => row.id}
            rowBorders={false}
            outerBorder={false}
            enableColumnResizing
        />
    );
};
